/**
 * MobilePreview.js — Mobile preview panel.
 *
 * Consumes backend APIs only:
 *   GET /api/expo/{id}/preview/status  — preview_status, build_status, phase_label, qr_ready, url
 *   GET /api/expo/{id}/qr              — QR PNG (202 while building, 200 when ready)
 *   GET /api/expo/{id}/qr/url          — raw Expo URL for copy
 *   GET /api/expo/{id}/logs            — build logs
 *   POST /api/expo/{id}/start          — start runtime
 *   POST /api/expo/{id}/restart        — restart runtime
 *   POST /api/expo/{id}/stop           — stop runtime
 *
 * Live reload after AI code changes is handled by the orchestrator (_smart_update).
 * This component only polls — it never pushes file changes.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { authFetch } from '../services/api';

// ─── Constants ────────────────────────────────────────────────────────────────
const POLL_MS     = 3000;
const LOG_POLL_MS = 2500;

// preview_status → dot colour
const STATUS_COLOR = {
  loading:  '#f59e0b',
  building: '#f59e0b',
  ready:    '#10b981',
  failed:   '#f87171',
};

// preview_status → human label for the phone screen
const STATUS_LABEL = {
  loading:  'Starting…',
  building: 'Building…',
  ready:    'Running',
  failed:   'Failed',
};

// ─── Component ────────────────────────────────────────────────────────────────
export default function MobilePreview({ projectId, projectName, files }) {
  const [preview,   setPreview]   = useState(null);   // /preview/status response
  const [qrSrc,     setQrSrc]     = useState(null);   // object URL for QR PNG
  const [logs,      setLogs]      = useState([]);
  const [logsOpen,  setLogsOpen]  = useState(false);
  const [lastLogTs, setLastLogTs] = useState(0);
  const [copied,    setCopied]    = useState(false);
  const [device,    setDevice]    = useState('ios');
  const [guideOpen, setGuideOpen] = useState(true);

  const startedRef  = useRef(false);
  const inFlightRef = useRef(false);
  const logsEndRef  = useRef(null);
  const qrUrlRef    = useRef(null);   // tracks last QR URL to avoid re-fetching same PNG

  const fileCount = Object.keys(files || {}).length;

  // ── Start runtime once files are available ──────────────────────────────────
  const startRuntime = useCallback(async () => {
    if (!projectId || fileCount === 0 || inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const res = await authFetch(`/api/expo/${projectId}/start`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        if (!data.skipped) addLog('🚀 Runtime starting…');
      }
    } catch (_) {}
    finally { inFlightRef.current = false; }
  }, [projectId, fileCount]);

  useEffect(() => {
    if (fileCount > 0 && projectId && !startedRef.current) {
      startedRef.current = true;
      startRuntime();
    }
  }, [fileCount, projectId, startRuntime]);

  // ── Fetch QR PNG from /api/expo/{id}/qr ────────────────────────────────────
  const fetchQr = useCallback(async () => {
    try {
      const res = await authFetch(`/api/expo/${projectId}/qr`);
      if (res.status === 200) {
        const blob = await res.blob();
        setQrSrc(prev => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
      }
    } catch (_) {}
  }, [projectId]);

  // ── Poll /preview/status ────────────────────────────────────────────────────
  useEffect(() => {
    if (!projectId || fileCount === 0) return;
    let alive = true;

    const poll = async () => {
      try {
        const res = await authFetch(`/api/expo/${projectId}/preview/status`);
        if (!res.ok || !alive) return;
        const data = await res.json();
        setPreview(data);

        // Fetch QR PNG when ready — qr_ready is a boolean, track via url sentinel
        const currentUrl = data.url || null;
        if (data.qr_ready && currentUrl && currentUrl !== qrUrlRef.current) {
          qrUrlRef.current = currentUrl;
          fetchQr();
        } else if (data.qr_ready && !currentUrl && qrUrlRef.current !== 'pending') {
          // qr_ready but url not yet synced — fetch anyway
          qrUrlRef.current = 'pending';
          fetchQr();
        }
        // Clear QR when no longer ready
        if (!data.qr_ready && qrUrlRef.current) {
          setQrSrc(null);
          qrUrlRef.current = null;
        }
      } catch (_) {}
    };

    poll();
    const id = setInterval(poll, POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [projectId, fileCount, fetchQr]);

  // ── Poll logs ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!projectId || fileCount === 0) return;
    let alive = true;

    const poll = async () => {
      try {
        const res = await authFetch(`/api/expo/${projectId}/logs?since=${lastLogTs}`);
        if (!res.ok || !alive) return;
        const data = await res.json();
        if (data.logs?.length > 0) {
          setLogs(prev => [...prev, ...data.logs].slice(-600));
          setLastLogTs(data.logs[data.logs.length - 1].t);
        }
      } catch (_) {}
    };

    const id = setInterval(poll, LOG_POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [projectId, fileCount, lastLogTs]);

  // ── Auto-scroll logs ────────────────────────────────────────────────────────
  useEffect(() => {
    if (logsOpen) logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs, logsOpen]);

  // ── Cleanup blob URL on unmount ─────────────────────────────────────────────
  useEffect(() => () => { if (qrSrc) URL.revokeObjectURL(qrSrc); }, [qrSrc]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  function addLog(msg) {
    setLogs(prev => [...prev.slice(-599), { t: Date.now() / 1000, msg, level: 'info' }]);
  }

  const handleCopy = async () => {
    try {
      const res = await authFetch(`/api/expo/${projectId}/qr/url`);
      if (res.ok) {
        const { url } = await res.json();
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (_) {}
  };

  const handleRestart = async () => {
    setQrSrc(null);
    qrUrlRef.current = null;
    addLog('↺ Restarting…');
    try { await authFetch(`/api/expo/${projectId}/restart`, { method: 'POST' }); } catch (_) {}
  };

  const handleStop = async () => {
    startedRef.current  = false;
    inFlightRef.current = false;
    setQrSrc(null);
    qrUrlRef.current = null;
    try { await authFetch(`/api/expo/${projectId}/stop`, { method: 'POST' }); } catch (_) {}
    setPreview(p => p ? { ...p, preview_status: 'loading', build_status: 'idle', url: null, qr_ready: false } : p);
    addLog('⏹ Stopped');
  };

  const handleRetry = async () => {
    startedRef.current  = false;
    inFlightRef.current = false;
    setQrSrc(null);
    qrUrlRef.current = null;
    setLogs([]);
    setLastLogTs(0);
    setPreview(null);
    await new Promise(r => setTimeout(r, 150));
    startedRef.current = true;
    startRuntime();
  };

  // ── Derived state ───────────────────────────────────────────────────────────
  const ps          = preview?.preview_status || 'loading';
  const bs          = preview?.build_status   || 'idle';
  const phaseLabel  = preview?.phase_label    || STATUS_LABEL[ps] || ps;
  const expoUrl     = preview?.url            || null;
  const error       = preview?.error          || null;
  const isReady     = ps === 'ready';
  const isBuilding  = ps === 'building' || ps === 'loading';
  const isFailed    = ps === 'failed';
  const dotColor    = STATUS_COLOR[ps] || '#475569';

  if (fileCount === 0) {
    return (
      <div style={s.empty}>
        <span style={{ fontSize: 48 }}>📱</span>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 12, textAlign: 'center' }}>
          No mobile files yet.<br />Use the chat to generate your React Native app.
        </p>
      </div>
    );
  }

  return (
    <div style={s.root}>

      {/* ══════════════ LEFT PANEL ══════════════ */}
      <div style={s.leftPanel}>

        {/* Header */}
        <div style={s.panelHeader}>
          <span style={s.panelTitle}>📱 Expo Preview</span>
          <span style={{ ...s.dot, background: dotColor,
            animation: isBuilding ? 'mblPulse 1.2s infinite' : 'none' }} />
        </div>

        {/* QR box */}
        <div style={s.qrBox}>
          {isReady && qrSrc ? (
            <img src={qrSrc} alt="Expo QR" style={s.qrImg} />
          ) : isBuilding ? (
            <div style={s.qrCenter}>
              <Spinner size={28} />
              <p style={s.qrHint}>{phaseLabel}</p>
            </div>
          ) : isFailed ? (
            <div style={s.qrCenter}>
              <span style={{ fontSize: 28 }}>⚠️</span>
              <p style={{ ...s.qrHint, color: '#f87171' }}>Build failed</p>
            </div>
          ) : (
            <div style={s.qrCenter}>
              <span style={{ fontSize: 28 }}>📷</span>
              <p style={s.qrHint}>QR will appear here</p>
            </div>
          )}
        </div>

        {isReady && <p style={s.scanHint}>Scan with <strong>Expo Go</strong></p>}

        {/* Expo Go guide */}
        <ExpoGoGuide open={guideOpen} onToggle={() => setGuideOpen(o => !o)} />

        {/* URL + copy */}
        {expoUrl && (
          <div style={s.urlRow}>
            <span style={s.urlText} title={expoUrl}>
              {expoUrl.length > 34 ? expoUrl.slice(0, 34) + '…' : expoUrl}
            </span>
            <button onClick={handleCopy} style={s.copyBtn}>{copied ? '✓' : 'Copy'}</button>
          </div>
        )}

        {/* Status card */}
        <div style={s.statusCard}>
          <InfoRow label="Runtime" value={<span style={{ fontSize: 10, color: dotColor, fontWeight: 700 }}>{ps}</span>} />
          <InfoRow label="Phase"   value={<span style={{ fontSize: 10, color: dotColor }}>{phaseLabel}</span>} />
          <InfoRow label="Build"   value={<BuildBadge bs={bs} />} />
          <InfoRow label="Files"   value={<span style={{ color: '#94a3b8', fontSize: 10 }}>{fileCount}</span>} />
        </div>

        {/* Error */}
        {isFailed && error && (
          <div style={s.errorBox}><p style={s.errorText}>{error}</p></div>
        )}

        {/* Actions */}
        <div style={s.btnRow}>
          {(isFailed || !preview) && (
            <button onClick={handleRetry} style={s.btnPrimary}>
              {isFailed ? '🔄 Retry' : '▶ Start'}
            </button>
          )}
          {isReady && (
            <button onClick={handleRestart} style={s.btnSecondary}>↺ Restart</button>
          )}
          {(isReady || isBuilding) && (
            <button onClick={handleStop} style={s.btnDanger}>⏹ Stop</button>
          )}
        </div>

        {/* Logs */}
        <div style={s.logsSection}>
          <button onClick={() => setLogsOpen(o => !o)} style={s.logsToggle}>
            {logsOpen ? '▾' : '▸'} Build Logs
            {logs.length > 0 && <span style={s.logsBadge}>{logs.length}</span>}
          </button>
          {logsOpen && (
            <div style={s.logsBox}>
              {logs.length === 0
                ? <span style={s.logLine}>No logs yet…</span>
                : logs.map((e, i) => (
                    <div key={i} style={{ ...s.logLine, color: logColor(e) }}>{e.msg}</div>
                  ))
              }
              <div ref={logsEndRef} />
            </div>
          )}
        </div>
      </div>

      {/* ══════════════ RIGHT PANEL — PHONE FRAME ══════════════ */}
      <div style={s.rightPanel}>

        <div style={s.deviceToggle}>
          {['ios', 'android'].map(d => (
            <button key={d} onClick={() => setDevice(d)}
              style={{ ...s.toggleBtn, ...(device === d ? s.toggleActive : {}) }}>
              {d === 'ios' ? '🍎 iOS' : '🤖 Android'}
            </button>
          ))}
        </div>

        <div style={s.phoneWrap}>
          <div style={{ ...s.phone, ...(device === 'android' ? s.phoneAndroid : {}) }}>

            {/* Status bar */}
            {device === 'ios'
              ? <div style={s.notch}><div style={s.notchPill} /></div>
              : <div style={s.androidBar}><span style={{ fontSize: 9, color: '#aaa' }}>9:41</span></div>
            }

            {/* Screen */}
            <div style={s.screen}>
              {isBuilding && (
                <div style={s.overlay}>
                  <Spinner size={40} />
                  <p style={s.screenMsg}>{phaseLabel}</p>
                  <p style={s.screenSub}>This may take 30–90 seconds</p>
                </div>
              )}
              {isFailed && (
                <div style={s.overlay}>
                  <span style={{ fontSize: 32 }}>⚠️</span>
                  <p style={{ ...s.screenMsg, color: '#f87171', marginTop: 10 }}>
                    {error || 'Build failed'}
                  </p>
                  <button onClick={handleRetry} style={s.retryBtn}>🔄 Retry</button>
                </div>
              )}
              {!preview && !isFailed && (
                <div style={s.overlay}>
                  <span style={{ fontSize: 40 }}>📱</span>
                  <p style={s.screenMsg}>Preview will appear here</p>
                  <p style={s.screenSub}>once Expo starts</p>
                </div>
              )}
              {isReady && (
                <div style={s.overlay}>
                  {qrSrc
                    ? <>
                        <img src={qrSrc} alt="QR" style={{ width: 140, height: 140, borderRadius: 8 }} />
                        <p style={{ ...s.screenMsg, marginTop: 12, fontSize: 11 }}>
                          Scan with <strong style={{ color: '#6366f1' }}>Expo Go</strong>
                        </p>
                        {expoUrl && (
                          <p style={{ ...s.screenSub, marginTop: 6, fontFamily: 'monospace', fontSize: 9, color: '#6366f1' }}>
                            {expoUrl}
                          </p>
                        )}
                      </>
                    : <>
                        <Spinner size={36} />
                        <p style={s.screenMsg}>Generating QR…</p>
                      </>
                  }
                </div>
              )}
            </div>

            {/* Home bar */}
            <div style={s.homeBar}>
              {device === 'ios'
                ? <div style={s.homeIndicator} />
                : <div style={{ display: 'flex', gap: 14 }}>
                    {['◀', '●', '■'].map(c => <span key={c} style={s.androidBtn}>{c}</span>)}
                  </div>
              }
            </div>
          </div>
        </div>

        <p style={s.caption}>
          {isReady    ? `✅ Expo running · ${fileCount} files · scan QR with Expo Go`
          : isBuilding ? `⏳ ${phaseLabel} · ${fileCount} files`
          : isFailed   ? `❌ Build failed · ${fileCount} files`
          :              `📱 ${fileCount} files`}
        </p>
      </div>

      <style>{`
        @keyframes mblSpin  { to { transform: rotate(360deg); } }
        @keyframes mblPulse { 0%,100% { opacity:1; } 50% { opacity:0.25; } }
      `}</style>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

const EXPO_STEPS = [
  {
    title: 'Install Expo Go',
    body: 'Download the free Expo Go app on your phone.',
  },
  {
    title: 'Same Wi-Fi network',
    body: 'Connect your phone and this computer to the same Wi-Fi network.',
  },
  {
    title: 'Scan the QR code',
    body: 'iOS — open the Camera app and point it at the QR.\nAndroid — open Expo Go → tap "Scan QR code".',
  },
  {
    title: 'App opens on your phone',
    body: 'Your generated app loads live. AI changes hot-reload automatically.',
  },
];

function ExpoGoGuide({ open, onToggle }) {
  return (
    <div style={g.wrap}>
      <button onClick={onToggle} style={g.header}>
        <span style={g.headerTitle}>How to use Expo Go</span>
        <span style={g.chevron}>{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div style={g.body}>
          {EXPO_STEPS.map((step, i) => (
            <React.Fragment key={i}>
              <div style={g.step}>
                <div style={g.stepLeft}>
                  <div style={g.stepNum}>{i + 1}</div>
                </div>
                <div style={g.stepContent}>
                  <span style={g.stepTitle}>{step.title}</span>
                  <p style={g.stepBody}>{step.body}</p>
                </div>
              </div>
              {i < EXPO_STEPS.length - 1 && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ width: 20, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                    <div style={g.stepLine} />
                  </div>
                  <div style={{ flex: 1 }} />
                </div>
              )}
            </React.Fragment>
          ))}

          <div style={g.tip}>
            <span style={g.tipText}>
              Can't scan? Tap <strong>Copy</strong> above and paste the URL into
              Expo Go → <em>Enter URL manually</em>.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function Spinner({ size = 24 }) {
  const b = Math.max(2, Math.round(size / 10));
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      border: `${b}px solid #1e293b`, borderTopColor: '#6366f1',
      animation: 'mblSpin 0.8s linear infinite',
    }} />
  );
}

function BuildBadge({ bs }) {
  const cfg = {
    idle:       ['—',          '#64748b'],
    loading:    ['Loading…',   '#f59e0b'],
    installing: ['Installing…','#f59e0b'],
    building:   ['Building…',  '#f59e0b'],
    ready:      ['✅ Ready',   '#10b981'],
    failed:     ['❌ Failed',  '#f87171'],
  }[bs] || ['—', '#64748b'];
  return <span style={{ fontSize: 10, color: cfg[1], fontWeight: 600 }}>{cfg[0]}</span>;
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0' }}>
      <span style={{ fontSize: 10, color: '#64748b' }}>{label}</span>
      {value}
    </div>
  );
}

function logColor(e) {
  const m = e.msg || '';
  if (e.level === 'error' || m.includes('❌') || m.includes('[err]')) return '#f87171';
  if (m.includes('✅'))                                                return '#10b981';
  if (e.level === 'warn'  || m.includes('⚠️') || m.includes('⏳'))   return '#f59e0b';
  if (m.includes('🔄') || m.includes('🚀') || m.includes('🔧'))      return '#a5b4fc';
  return '#94a3b8';
}

// ─── Expo Go guide styles ────────────────────────────────────────────────────
const g = {
  wrap: {
    borderRadius: 8,
    border: '1px solid rgba(99,102,241,0.25)',
    background: 'rgba(99,102,241,0.06)',
    overflow: 'hidden',
  },
  header: {
    width: '100%', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', padding: '7px 10px',
    background: 'none', border: 'none', cursor: 'pointer',
  },
  headerTitle: { fontSize: 11, fontWeight: 700, color: '#a5b4fc' },
  chevron:     { fontSize: 11, color: '#6366f1' },

  body: { padding: '8px 10px 10px', display: 'flex', flexDirection: 'column', gap: 0, maxHeight: 240, overflowY: 'auto' },

  step:     { display: 'flex', gap: 8, alignItems: 'flex-start' },
  stepLeft: { display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20, flexShrink: 0, paddingTop: 1 },
  stepNum:  {
    width: 18, height: 18, borderRadius: '50%', background: '#6366f1',
    color: '#fff', fontSize: 9, fontWeight: 800, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  stepLine: { width: 1, background: 'rgba(99,102,241,0.2)', flexGrow: 1, minHeight: 10, margin: '3px 0' },

  stepContent: { flex: 1, paddingBottom: 10 },
  stepTitle:   { fontSize: 11, fontWeight: 600, color: '#c7d2fe', display: 'block', marginBottom: 3, lineHeight: 1.3 },
  stepBody:    { fontSize: 10, color: '#64748b', margin: 0, lineHeight: 1.6, whiteSpace: 'pre-line' },

  tip: {
    background: 'rgba(245,158,11,0.07)',
    border: '1px solid rgba(245,158,11,0.18)',
    borderRadius: 5, padding: '6px 8px', marginTop: 6,
  },
  tipText: { fontSize: 10, color: '#d97706', lineHeight: 1.5 },
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const PH = 520, PW = 258;

const s = {
  root:  { display: 'flex', height: '100%', overflow: 'hidden', background: 'var(--bg-primary)' },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
           height: '100%', gap: 8, padding: 24 },

  leftPanel: {
    width: 224, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10,
    borderRight: '1px solid var(--border)', padding: '14px 12px',
    overflowY: 'auto', background: 'var(--bg-secondary)',
  },
  panelHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  panelTitle:  { fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' },
  dot:         { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },

  qrBox: {
    width: '100%', aspectRatio: '1', background: '#fff', borderRadius: 10,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden', border: '1px solid var(--border)',
  },
  qrImg:    { width: '100%', height: '100%', objectFit: 'contain', display: 'block' },
  qrCenter: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: 12 },
  qrHint:   { fontSize: 10, color: '#94a3b8', textAlign: 'center', margin: 0 },
  scanHint: { fontSize: 10, color: '#94a3b8', textAlign: 'center', margin: 0, lineHeight: 1.5 },

  urlRow: {
    display: 'flex', alignItems: 'center', gap: 4,
    background: 'var(--bg-input,#0f172a)', borderRadius: 6,
    padding: '4px 8px', border: '1px solid var(--border)',
  },
  urlText: { flex: 1, fontSize: 9, color: '#94a3b8', fontFamily: 'monospace',
             overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  copyBtn: { padding: '2px 8px', background: '#6366f1', color: '#fff',
             border: 'none', borderRadius: 4, fontSize: 9, fontWeight: 700, cursor: 'pointer', flexShrink: 0 },

  statusCard: {
    background: 'var(--bg-input,#0f172a)', borderRadius: 8,
    padding: '8px 10px', border: '1px solid var(--border)',
  },
  errorBox:  { background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)',
               borderRadius: 6, padding: '6px 8px' },
  errorText: { fontSize: 10, color: '#f87171', margin: 0, lineHeight: 1.4, wordBreak: 'break-word' },

  btnRow:       { display: 'flex', gap: 6, flexWrap: 'wrap' },
  btnPrimary:   { flex: 1, padding: '7px 0', background: '#6366f1', color: '#fff',
                  border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' },
  btnSecondary: { flex: 1, padding: '7px 0', background: 'rgba(99,102,241,0.12)', color: '#a5b4fc',
                  border: '1px solid rgba(99,102,241,0.3)', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' },
  btnDanger:    { flex: 1, padding: '7px 0', background: 'rgba(248,113,113,0.1)', color: '#f87171',
                  border: '1px solid rgba(248,113,113,0.25)', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' },

  logsSection: { display: 'flex', flexDirection: 'column', gap: 4 },
  logsToggle:  { display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
                 color: 'var(--text-muted)', fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: '2px 0' },
  logsBadge:   { background: 'rgba(99,102,241,0.2)', color: '#a5b4fc',
                 borderRadius: 10, padding: '1px 6px', fontSize: 9, fontWeight: 700 },
  logsBox:     { background: '#060d18', borderRadius: 6, padding: '8px', maxHeight: 200,
                 overflowY: 'auto', border: '1px solid var(--border)',
                 display: 'flex', flexDirection: 'column', gap: 1 },
  logLine:     { fontSize: 9, fontFamily: 'monospace', lineHeight: 1.6, wordBreak: 'break-all' },

  rightPanel: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                padding: '14px 0 20px', overflow: 'auto', gap: 10 },
  deviceToggle: { display: 'flex', background: 'var(--bg-secondary)',
                  border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' },
  toggleBtn:    { padding: '6px 16px', background: 'transparent', border: 'none',
                  color: 'var(--text-muted)', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  toggleActive: { background: 'rgba(99,102,241,0.15)', color: 'var(--accent-light,#a5b4fc)' },

  phoneWrap: { filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.5))' },
  phone: {
    width: PW, height: PH, background: '#1a1a1a', borderRadius: 42,
    border: '3px solid #2a2a2a', display: 'flex', flexDirection: 'column',
    overflow: 'hidden', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.05)',
  },
  phoneAndroid: { borderRadius: 22 },

  notch:       { height: 30, background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  notchPill:   { width: 70, height: 9, background: '#000', borderRadius: 10, border: '1px solid #2a2a2a' },
  androidBar:  { height: 22, background: '#111', display: 'flex', alignItems: 'center',
                 justifyContent: 'flex-end', paddingRight: 10, flexShrink: 0 },

  screen:  { flex: 1, background: '#0a0f1a', overflow: 'hidden', position: 'relative' },
  overlay: {
    position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', background: '#0a0f1a', padding: 16,
  },
  screenMsg: { color: '#94a3b8', fontSize: 12, marginTop: 14, textAlign: 'center', fontWeight: 600 },
  screenSub: { color: '#475569', fontSize: 10, marginTop: 4, textAlign: 'center' },
  retryBtn:  { marginTop: 14, padding: '7px 18px', background: '#6366f1', color: '#fff',
               border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' },

  homeBar:       { height: 30, background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  homeIndicator: { width: 88, height: 4, background: '#333', borderRadius: 4 },
  androidBtn:    { fontSize: 11, color: '#555', cursor: 'pointer' },

  caption: { fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', padding: '0 12px' },
};
